import React from "react";
import { FunnelStage } from "../analyticsApi";
import { dropOffPct, formatNumber, formatPct, funnelWidth } from "../analyticsUtils";
import { seriesColors, useIsDark } from "./ui";

interface Props {
  stages: FunnelStage[];
  /** Previous-period count per stage key, shown small under the stage. */
  previous?: Record<string, number | null | undefined>;
}

/**
 * Horizontal bars relative to the first stage, with the drop-off between
 * consecutive count stages. A "%" stage is a figure, not a bar.
 */
export default function Funnel({ stages, previous }: Props) {
  const isDark = useIsDark();
  const color = seriesColors(isDark)[0];
  const first = stages.find((s) => s.unit !== "%")?.count ?? null;
  let previousCount: number | null = null;
  return (
    <ol className="space-y-3">
      {stages.map((stage) => {
        if (stage.unit === "%") {
          return (
            <li key={stage.key} className="flex items-center justify-between gap-3 pt-2 border-t border-dashed border-slate-200 dark:border-slate-700">
              <span className="text-xs text-slate-500 dark:text-slate-400">{stage.label}</span>
              <span className="text-lg font-bold tabular-nums text-slate-800 dark:text-slate-100">{formatPct(stage.count)}</span>
            </li>
          );
        }
        const drop = dropOffPct(previousCount, stage.count);
        const width = funnelWidth(stage.count, first);
        const prev = previous?.[stage.key];
        previousCount = stage.count;
        return (
          <li key={stage.key}>
            <div className="flex items-baseline justify-between gap-3 text-xs mb-1">
              <span className="text-slate-500 dark:text-slate-400">{stage.label}</span>
              <span className="tabular-nums">
                <span className="font-semibold text-slate-800 dark:text-slate-100">{formatNumber(stage.count)}</span>
                {drop != null && <span className="ml-2 text-rose-500 dark:text-rose-400">−{formatPct(drop)} drop</span>}
              </span>
            </div>
            <div className="h-6 rounded-md bg-slate-100 dark:bg-slate-700/60 overflow-hidden" title={`${stage.label}: ${formatNumber(stage.count)}`}>
              <div className="h-full rounded-md" style={{ width: `${width}%`, backgroundColor: color, minWidth: width > 0 ? 4 : 0 }} />
            </div>
            {previous && (
              <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5 tabular-nums">Previous period: {formatNumber(prev ?? null)}</p>
            )}
          </li>
        );
      })}
    </ol>
  );
}
